import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import {
  ShoppingCart, Heart, Share2, Package, 
  Truck, Shield, CheckCircle, AlertCircle,
  Home, ChevronRight, Loader2, Tag, Info
} from 'lucide-react';
import { API_URL } from '../config/api';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [stockInfo, setStockInfo] = useState(null);
  const [checkingStock, setCheckingStock] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  useEffect(() => {
    if (product) {
      checkStockAvailability();
    }
  }, [product, selectedVariant, quantity]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/products/${productId}`);
      const result = await response.json();
      
      if (result.success) {
        setProduct(result.data);
        setVariants(result.data.variants || []);
        
        if (result.data.variants && result.data.variants.length > 0) {
          setSelectedVariant(result.data.variants[0]);
        }
      } else {
        setError('Product not found');
      }
    } catch (err) {
      console.error('Error fetching product:', err);
      setError('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const checkStockAvailability = async () => {
    try {
      setCheckingStock(true);
      const params = new URLSearchParams({
        product_id: product.id,
        quantity: quantity.toString()
      });
      
      if (selectedVariant) {
        params.append('variant_id', selectedVariant.id);
      }
      
      const response = await fetch(`${API_URL}/api/inventory/check-availability?${params}`);
      const result = await response.json();
      
      if (result.success) {
        setStockInfo(result);
      }
    } catch (err) {
      console.error('Error checking stock:', err);
    } finally {
      setCheckingStock(false);
    }
  };

  const handleAddToCart = async () => {
    setAddingToCart(true);
    
    if (product.has_variants && variants.length > 0 && !selectedVariant) {
      alert('Please select a variant');
      setAddingToCart(false);
      return;
    }
    
    if (stockInfo && !stockInfo.available) {
      alert(`Only ${stockInfo.total_available} items available in stock`);
      setAddingToCart(false);
      return;
    }
    
    const result = await addToCart(product, selectedVariant);
    
    if (result.redirected) {
      return;
    }
    
    if (result.success) {
      alert(`✅ ${result.message || 'Added to cart!'}`);
      
      if (quantity > 1) {
        for (let i = 1; i < quantity; i++) {
          await addToCart(product, selectedVariant);
        }
      }
      
      setTimeout(() => {
        setAddingToCart(false);
        navigate('/cart');
      }, 300);
    } else if (result.error) {
      alert(`❌ ${result.error}`);
      setAddingToCart(false);
    } else {
      setAddingToCart(false);
    }
  };

  const calculateEffectivePrice = () => {
    if (selectedVariant) {
      return product.price + (selectedVariant.price_adjustment || 0);
    }
    return product.price;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-600 animate-spin mx-auto mb-4" />
          <p className="text-sm font-medium text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <Package className="w-24 h-24 text-gray-300 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 mb-3">{error || 'Product Not Found'}</h2>
          <p className="text-gray-600 mb-6">The product you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/')}
            className="px-8 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors"
          >
            Back to Store
          </button>
        </div>
      </div>
    );
  }

  const effectivePrice = calculateEffectivePrice();
  const savings = Math.round(effectivePrice * 0.2);
  const originalPrice = effectivePrice + savings;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-600 hover:text-orange-600 transition-colors flex items-center gap-1.5">
              <Home className="w-4 h-4" />
              <span>Home</span>
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <Link to={`/categories/${product.category}/products`} className="text-gray-600 hover:text-orange-600 transition-colors">
              {product.category}
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className="text-gray-900 font-semibold truncate max-w-xs">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          {/* LEFT: Image Gallery */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 sticky top-6">
              <div className="aspect-square bg-white rounded-xl flex items-center justify-center overflow-hidden mb-6">
                {(selectedVariant?.image_url || product.image_url) ? (
                  <img 
                    src={selectedVariant?.image_url || product.image_url} 
                    alt={product.name}
                    className="w-full h-full object-contain p-4 product-img-clean"
                  />
                ) : (
                  <Package className="w-32 h-32 text-gray-300" />
                )}
              </div>
              
              {/* Quick Actions */}
              <div className="grid grid-cols-2 gap-3">
                <button className="py-3 border border-gray-200 rounded-lg flex items-center justify-center gap-2 hover:border-orange-500 hover:bg-orange-50 hover:text-orange-600 transition-all group">
                  <Heart className="w-5 h-5 group-hover:fill-orange-500" />
                  <span className="font-semibold text-sm">Wishlist</span>
                </button>
                <button className="py-3 border border-gray-200 rounded-lg flex items-center justify-center gap-2 hover:border-orange-500 hover:bg-orange-50 hover:text-orange-600 transition-all">
                  <Share2 className="w-5 h-5" />
                  <span className="font-semibold text-sm">Share</span>
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT: Product Details */}
          <div className="space-y-6">
            
            {/* Category Badge & Title */}
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-700 px-3 py-1.5 rounded-lg mb-4">
                <Tag className="w-3.5 h-3.5" />
                <span className="text-xs font-bold uppercase tracking-wider">{product.category}</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-black text-gray-900 leading-tight mb-3">{product.name}</h1>
              {product.description && (
                <p className="text-gray-600 leading-relaxed">{product.description}</p>
              )}
            </div>

            {/* Pricing Card */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-6 border border-orange-200">
              <div className="flex items-end gap-4 mb-3">
                <span className="text-5xl font-black text-gray-900">
                  ₹{effectivePrice.toLocaleString()}
                </span>
                <span className="text-2xl text-gray-400 line-through mb-2">
                  ₹{originalPrice.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm font-bold">
                  <CheckCircle className="w-4 h-4" />
                  Save ₹{savings.toLocaleString()}
                </span>
                <span className="text-sm font-semibold text-orange-800">
                  20% Off Today!
                </span>
              </div>
            </div>

            {/* Variant Selection */}
            {product.has_variants && variants.length > 0 && (
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <label className="block text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                  Choose Variant
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {variants.map((variant) => (
                    <button
                      key={variant.id}
                      onClick={() => setSelectedVariant(variant)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        selectedVariant?.id === variant.id
                          ? 'border-orange-500 bg-orange-50 shadow-sm'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-bold text-gray-900 mb-1">{variant.variant_name}</div>
                      {variant.price_adjustment !== 0 && (
                        <div className="text-sm text-gray-600">
                          {variant.price_adjustment > 0 ? '+' : ''}₹{Math.abs(variant.price_adjustment)}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                {selectedVariant && (
                  <div className="mt-4 flex items-center gap-2 text-sm bg-green-50 text-green-700 px-3 py-2 rounded-lg">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-semibold">Selected: {selectedVariant.variant_name}</span>
                  </div>
                )}
              </div>
            )}

            {/* Quantity & Stock */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <label className="block text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                Quantity
              </label>
              <div className="flex items-center gap-6">
                {/* Quantity Selector */}
                <div className="flex items-center border-2 border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 flex items-center justify-center bg-gray-50 hover:bg-gray-100 text-gray-700 font-bold text-xl transition-colors"
                  >
                    −
                  </button>
                  <span className="w-16 text-center font-bold text-xl text-gray-900">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    disabled={stockInfo && quantity >= stockInfo.total_available}
                    className="w-12 h-12 flex items-center justify-center bg-gray-50 hover:bg-gray-100 disabled:bg-gray-200 disabled:cursor-not-allowed text-gray-700 font-bold text-xl transition-colors"
                  >
                    +
                  </button>
                </div>
                
                {/* Stock Badge */}
                <div className="flex-1">
                  {checkingStock ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Checking availability...</span>
                    </div>
                  ) : stockInfo ? (
                    stockInfo.available ? (
                      <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg">
                        <CheckCircle className="w-5 h-5" />
                        <div>
                          <div className="font-bold text-sm">In Stock</div>
                          <div className="text-xs">{stockInfo.total_available} units available</div>
                        </div>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-2 bg-red-50 text-red-700 px-4 py-2 rounded-lg">
                        <AlertCircle className="w-5 h-5" />
                        <div>
                          <div className="font-bold text-sm">Low Stock</div>
                          <div className="text-xs">Only {stockInfo.total_available} left</div>
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-bold text-sm">In Stock</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Warehouse Details */}
              {stockInfo && stockInfo.warehouses && stockInfo.warehouses.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                    <Info className="w-3.5 h-3.5" />
                    <span>Available at {stockInfo.warehouses.length} location{stockInfo.warehouses.length > 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {stockInfo.warehouses.map((wh, idx) => (
                      wh.available > 0 && (
                        <span key={idx} className="text-xs bg-blue-50 text-blue-700 font-semibold px-3 py-1.5 rounded-lg">
                          {wh.warehouse_name} ({wh.available})
                        </span>
                      )
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Add to Cart CTA */}
            <button
              onClick={handleAddToCart}
              disabled={addingToCart || (stockInfo && !stockInfo.available)}
              className="w-full py-5 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed text-white text-lg font-black uppercase tracking-wider rounded-xl transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
            >
              {addingToCart ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span>Adding to Cart...</span>
                </>
              ) : stockInfo && !stockInfo.available ? (
                <>
                  <AlertCircle className="w-6 h-6" />
                  <span>Out of Stock</span>
                </>
              ) : (
                <>
                  <ShoppingCart className="w-6 h-6" />
                  <span>Add to Cart</span>
                </>
              )}
            </button>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="flex items-center gap-3 bg-white p-4 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                  <Truck className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <div className="font-bold text-sm text-gray-900">Free Delivery</div>
                  <div className="text-xs text-gray-500">On all orders</div>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white p-4 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <div className="font-bold text-sm text-gray-900">Secure Payment</div>
                  <div className="text-xs text-gray-500">100% protected</div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
